import { envelope_type_enum, insulation_material_category_enum, insulation_method_enum } from '@predium/enums';

export type WindowDoorEnvelopeType = envelope_type_enum.WINDOW | envelope_type_enum.DOOR;

export type OtherEnvelopeTypes = Exclude<envelope_type_enum, WindowDoorEnvelopeType>;

export type InsulationMaterial = {
  material_name: string;
  insulation_lambda: number;
  insulation_thickness: number;
  insulation_material_category: insulation_material_category_enum;
};

export type DoorTemplate<TMaterialName = DoorTemplateTypes> = {
  material_name: TMaterialName;
  u_value: number;
};

export type WindowTemplate = DoorTemplate<WindowTemplateTypes> & {
  insulation_lambda: number; // which is g_value!
};

/**
 * Insulation material templates which can be selected as templates by the user.
 */
export const enum InsulationMaterialTemplateTypes {
  Schaumglas = 'Schaumglas',
  Mineralschaumplatten = 'Mineralschaumplatten',
  Mineralwolle = 'Mineralwolle',
  Perlite = 'Perlite',
  Kalziumsilikatplatte = 'Kalziumsilikatplatte',
  Wärmedämmputz = 'Wärmedämmputz',
  Phenolharz_Hartschaum = 'Phenolharz_Hartschaum',
  PUR_Hartschaum = 'PUR_Hartschaum',
  PUR_Hartschaum_Alukaschiert = 'PUR_Hartschaum_Alukaschiert',
  PIR = 'PIR',
  EPS = 'EPS',
  XPS = 'XPS',
  Vakuumdaemmung = 'Vakuumdaemmung',
  Hanffaser = 'Hanffaser',
  Holzfaser = 'Holzfaser',
  Jute = 'Jute',
  Schafwolle = 'Schafwolle',
  Seegras = 'Seegras',
  Stroh = 'Stroh',
  Wiesengras = 'Wiesengras',
  Zellulose = 'Zellulose',
  Korkplatte = 'Korkplatte',
  Schilfplatten = 'Schilfplatten',
}

//properties of all materials in indexable object
export const InsulationMaterialTemplates: { [key in InsulationMaterialTemplateTypes]: InsulationMaterial } = {
  Schaumglas: {
    material_name: 'Schaumglas',
    insulation_lambda: 0.04,
    insulation_thickness: 0.16,
    insulation_material_category: insulation_material_category_enum.MINERAL,
  },
  Mineralschaumplatten: {
    material_name: 'Mineralschaumplatten',
    insulation_lambda: 0.045,
    insulation_thickness: 0.18,
    insulation_material_category: insulation_material_category_enum.MINERAL,
  },
  Mineralwolle: {
    material_name: 'Mineralwolle (Glaswolle / Steinwolle)',
    insulation_lambda: 0.035,
    insulation_thickness: 0.14,
    insulation_material_category: insulation_material_category_enum.MINERAL,
  },
  Perlite: {
    material_name: 'Perlite',
    insulation_lambda: 0.05,
    insulation_thickness: 0.2,
    insulation_material_category: insulation_material_category_enum.MINERAL,
  },
  Kalziumsilikatplatte: {
    material_name: 'Kalziumsilikatplatte',
    insulation_lambda: 0.07,
    insulation_thickness: 0.28,
    insulation_material_category: insulation_material_category_enum.MINERAL,
  },
  Wärmedämmputz: {
    material_name: 'Wärmedämmputz',
    insulation_lambda: 0.07,
    insulation_thickness: 0.03,
    insulation_material_category: insulation_material_category_enum.MINERAL,
  },

  //synthetic
  Phenolharz_Hartschaum: {
    material_name: 'Phenolharz-Hartschaum (PF)',
    insulation_lambda: 0.024,
    insulation_thickness: 0.09,
    insulation_material_category: insulation_material_category_enum.SYNTHETIC,
  },
  PUR_Hartschaum: {
    material_name: 'Hartschaum (PUR)',
    insulation_lambda: 0.025,
    insulation_thickness: 0.1,
    insulation_material_category: insulation_material_category_enum.SYNTHETIC,
  },
  PUR_Hartschaum_Alukaschiert: {
    material_name: 'Hartschaum (PUR) Alukaschiert',
    insulation_lambda: 0.025,
    insulation_thickness: 0.1,
    insulation_material_category: insulation_material_category_enum.SYNTHETIC,
  },
  EPS: {
    material_name: 'Expandiertes Polystyrol (EPS)',
    insulation_lambda: 0.035,
    insulation_thickness: 0.14,
    insulation_material_category: insulation_material_category_enum.SYNTHETIC,
  },
  XPS: {
    material_name: 'Extrudiertes Polystyrol (XPS)',
    insulation_lambda: 0.035,
    insulation_thickness: 0.14,
    insulation_material_category: insulation_material_category_enum.SYNTHETIC,
  },
  PIR: {
    material_name: 'Polyisocyanurate (PIR)',
    insulation_lambda: 0.024,
    insulation_thickness: 0.12,
    insulation_material_category: insulation_material_category_enum.SYNTHETIC,
  },
  Vakuumdaemmung: {
    material_name: 'Vakuumdämmung',
    insulation_lambda: 0.007,
    insulation_thickness: 0.04,
    insulation_material_category: insulation_material_category_enum.SYNTHETIC,
  },

  //renewable

  Hanffaser: {
    material_name: 'Hanffaser',
    insulation_lambda: 0.046,
    insulation_thickness: 0.18,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Holzfaser: {
    material_name: 'Holzfaserdämmstoff',
    insulation_lambda: 0.04,
    insulation_thickness: 0.16,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Jute: {
    material_name: 'Jute',
    insulation_lambda: 0.04,
    insulation_thickness: 0.16,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Schafwolle: {
    material_name: 'Schafwolle',
    insulation_lambda: 0.04,
    insulation_thickness: 0.16,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Seegras: {
    material_name: 'Seegras',
    insulation_lambda: 0.044,
    insulation_thickness: 0.18,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Stroh: {
    material_name: 'Stroh',
    insulation_lambda: 0.045,
    insulation_thickness: 0.18,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Wiesengras: {
    material_name: 'Wiesengras Zellulose',
    insulation_lambda: 0.04,
    insulation_thickness: 0.16,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Zellulose: {
    material_name: 'Zellulose',
    insulation_lambda: 0.04,
    insulation_thickness: 0.16,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Korkplatte: {
    material_name: 'Korkplatte',
    insulation_lambda: 0.05,
    insulation_thickness: 0.16,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
  Schilfplatten: {
    material_name: 'Schilfplatten',
    insulation_lambda: 0.065,
    insulation_thickness: 0.26,
    insulation_material_category: insulation_material_category_enum.RENEWABLE,
  },
};

export const enum WindowTemplateTypes {
  DoubleInsulatedWood = '2-fach Wärmeschutzverglasung (Holz)',
  DoubleInsulatedPlastic = '2-fach Wärmeschutzverglasung (Kunststoff)',
  DoubleInsulatedAluminium = '2-fach Wärmeschutzverglasung (Holz-Aluminium)',
  TripleInsulatedWood = '3-fach Wärmeschutzverglasung (Holz)',
  TripleInsulatedPlastic = '3-fach Wärmeschutzverglasung (Kunststoff)',
  TripleInsulatedAluminium = '3-fach Wärmeschutzverglasung (Holz-Aluminium)',
  TripleInsulatedPassiveAluminium = '3-fach Wärmeschutzverglasung, Passivhaus geeignet (Holz-Aluminium)',
  TripleInsulatedPassivePlastic = '3-fach Wärmeschutzverglasung, Passivhaus geeignet (Kunststoff)',
  TripleInsulatedPassiveWood = '3-fach Wärmeschutzverglasung, Passivhaus geeignet (Holz)',
}

export const WindowRenovationTemplates: WindowTemplate[] = [
  {
    material_name: WindowTemplateTypes.DoubleInsulatedWood,
    u_value: 1.1,
    insulation_lambda: 0.6,
  },
  {
    material_name: WindowTemplateTypes.DoubleInsulatedPlastic,
    u_value: 1.1,
    insulation_lambda: 0.6,
  },
  {
    material_name: WindowTemplateTypes.DoubleInsulatedAluminium,
    u_value: 1.1,
    insulation_lambda: 0.6,
  },
  {
    material_name: WindowTemplateTypes.TripleInsulatedWood,
    u_value: 0.9,
    insulation_lambda: 0.5,
  },
  {
    material_name: WindowTemplateTypes.TripleInsulatedPlastic,
    u_value: 0.9,
    insulation_lambda: 0.5,
  },
  {
    material_name: WindowTemplateTypes.TripleInsulatedAluminium,
    u_value: 0.9,
    insulation_lambda: 0.5,
  },
  {
    material_name: WindowTemplateTypes.TripleInsulatedPassiveAluminium,
    u_value: 0.7,
    insulation_lambda: 0.5,
  },
  {
    material_name: WindowTemplateTypes.TripleInsulatedPassivePlastic,
    u_value: 0.7,
    insulation_lambda: 0.5,
  },
  {
    material_name: WindowTemplateTypes.TripleInsulatedPassiveWood,
    u_value: 0.7,
    insulation_lambda: 0.5,
  },
] as const;

export const enum DoorTemplateTypes {
  SFH_11 = 'Neue Eingangstür Einfamilienhaus, Ud-Wert 1,1 (W/m2K)',
  SFH_13 = 'Neue Eingangstür Einfamilienhaus, Ud-Wert 1,3 (W/m2K)',
  MFH_11 = 'Neue Eingangstür Mehrfamilienhaus, Ud-Wert 1,1 (W/m2K)',
  MFH_13 = 'Neue Eingangstür Mehrfamilienhaus, Ud-Wert 1,3 (W/m2K)',
}

export const DoorRenovationTemplates: DoorTemplate<DoorTemplateTypes>[] = [
  {
    material_name: DoorTemplateTypes.SFH_11,
    u_value: 1.1,
  },
  {
    material_name: DoorTemplateTypes.SFH_13,
    u_value: 1.3,
  },
  {
    material_name: DoorTemplateTypes.MFH_11,
    u_value: 1.1,
  },
  {
    material_name: DoorTemplateTypes.MFH_13,
    u_value: 1.3,
  },
];

export const defaultMap: Record<OtherEnvelopeTypes, { [key in insulation_method_enum]?: string }> = {
  [envelope_type_enum.FLAT_ROOF]: {
    [insulation_method_enum.CORE_INSULATION_BOARDS]: InsulationMaterialTemplates.Holzfaser.material_name,
    [insulation_method_enum.INSULATION_GRADIENT]: InsulationMaterialTemplates.PUR_Hartschaum.material_name,
  },
  [envelope_type_enum.BASEMENT_CEILING]: {
    [insulation_method_enum.INSULATION_BOARDS_WITH_CLADDING]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.INSULATION_BOARDS_WITHOUT_CLADDING]: InsulationMaterialTemplates.Mineralwolle.material_name,
  },
  [envelope_type_enum.PITCHED_ROOF]: {
    [insulation_method_enum.ABOVE_RAFTER]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.BETWEEN_RAFTER]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.BELOW_RAFTER]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.INSULATION_GRADIENT]: InsulationMaterialTemplates.EPS.material_name,
  },
  [envelope_type_enum.TOP_FLOOR_CEILING]: {
    [insulation_method_enum.INSULATION_BOARDS_WALKABLE]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.INSULATION_BOARDS]: InsulationMaterialTemplates.Mineralwolle.material_name,
  },
  [envelope_type_enum.WALL]: {
    [insulation_method_enum.INTERIOR_PLASTER_SYSTEM]: InsulationMaterialTemplates.Mineralschaumplatten.material_name,
    [insulation_method_enum.FACING_SHELL]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.WDVS]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.VHF]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.CORE_INSULATION_BOARDS]: InsulationMaterialTemplates.Mineralwolle.material_name,
    [insulation_method_enum.BLOW_IN_INSULATION]: InsulationMaterialTemplates.Zellulose.material_name,
  },
  [envelope_type_enum.FLOOR]: {
    [insulation_method_enum.CORE_INSULATION_BOARDS]: InsulationMaterialTemplates.Mineralwolle.material_name,
  },
};

export function getEnvelopeInsulationDefaults(
  envelopeType: keyof typeof defaultMap,
  insulationMethod: insulation_method_enum,
): InsulationMaterial | undefined {
  const materialName = defaultMap[envelopeType][insulationMethod];
  return EnvelopeRenovationTemplates[envelopeType][insulationMethod]?.find(
    (template: InsulationMaterial) => template.material_name === materialName,
  );
}

export const EnvelopeRenovationTemplates: Record<
  OtherEnvelopeTypes,
  { [key in insulation_method_enum]?: InsulationMaterial[] }
> = {
  [envelope_type_enum.FLAT_ROOF]: {
    [insulation_method_enum.CORE_INSULATION_BOARDS]: [
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum_Alukaschiert,
        insulation_thickness: 0.14,
        insulation_lambda: 0.025,
      },
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum,
        insulation_thickness: 0.14,
        insulation_lambda: 0.025,
      },
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.16,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.18,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.2,
        insulation_lambda: 0.04,
      },
      {
        ...InsulationMaterialTemplates.XPS,
        insulation_thickness: 0.02,
        insulation_lambda: 0.042,
      },
    ],
    [insulation_method_enum.INSULATION_GRADIENT]: [
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.16,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum,
        insulation_thickness: 0.18,
        insulation_lambda: 0.025,
      },
    ],
  },
  [envelope_type_enum.PITCHED_ROOF]: {
    [insulation_method_enum.ABOVE_RAFTER]: [
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum,
        insulation_thickness: 0.1,
        insulation_lambda: 0.028,
      },
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.1,
        insulation_lambda: 0.032,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.16,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.043,
      },
    ],
    [insulation_method_enum.BETWEEN_RAFTER]: [
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.14,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.043,
      },
    ],
    [insulation_method_enum.BELOW_RAFTER]: [
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum,
        insulation_thickness: 0.1,
        insulation_lambda: 0.028,
      },
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.1,
        insulation_lambda: 0.032,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.12,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.043,
      },
    ],
  },
  [envelope_type_enum.BASEMENT_CEILING]: {
    [insulation_method_enum.INSULATION_BOARDS_WITH_CLADDING]: [
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum,
        insulation_thickness: 0.1,
        insulation_lambda: 0.028,
      },
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.1,
        insulation_lambda: 0.04,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.12,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.043,
      },
    ],
    [insulation_method_enum.INSULATION_BOARDS_WITHOUT_CLADDING]: [
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.1,
        insulation_lambda: 0.04,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.08,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.043,
      },
    ],
  },
  [envelope_type_enum.FLOOR]: {
    [insulation_method_enum.CORE_INSULATION_BOARDS]: [
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.08,
        insulation_lambda: 0.037,
      },
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum_Alukaschiert,
        insulation_thickness: 0.04,
        insulation_lambda: 0.024,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.08,
        insulation_lambda: 0.035,
      },
    ],
  },
  [envelope_type_enum.TOP_FLOOR_CEILING]: {
    [insulation_method_enum.INSULATION_BOARDS_WALKABLE]: [
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.1,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.18,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.04,
      },
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum,
        insulation_thickness: 0.08,
        insulation_lambda: 0.024,
      },
    ],
    [insulation_method_enum.INSULATION_BOARDS]: [
      {
        ...InsulationMaterialTemplates.PUR_Hartschaum,
        insulation_thickness: 0.08,
        insulation_lambda: 0.024,
      },
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.18,
        insulation_lambda: 0.04,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.16,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.08,
        insulation_lambda: 0.045,
      },
    ],
  },
  [envelope_type_enum.WALL]: {
    [insulation_method_enum.INTERIOR_PLASTER_SYSTEM]: [
      {
        ...InsulationMaterialTemplates.Mineralschaumplatten,
        insulation_thickness: 0.1,
        insulation_lambda: 0.04,
      },
      {
        ...InsulationMaterialTemplates.Vakuumdaemmung,
        insulation_thickness: 0.07,
        insulation_lambda: 0.024,
      },
    ],
    [insulation_method_enum.FACING_SHELL]: [
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.1,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.043,
      },
    ],
    [insulation_method_enum.WDVS]: [
      {
        ...InsulationMaterialTemplates.EPS,
        insulation_thickness: 0.1,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.16,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.16,
        insulation_lambda: 0.04,
      },
    ],
    [insulation_method_enum.BLOW_IN_INSULATION]: [
      {
        ...InsulationMaterialTemplates.Zellulose,
      },
    ],
    [insulation_method_enum.VHF]: [
      {
        ...InsulationMaterialTemplates.Mineralwolle,
        insulation_thickness: 0.1,
        insulation_lambda: 0.035,
      },
      {
        ...InsulationMaterialTemplates.Holzfaser,
        insulation_thickness: 0.1,
        insulation_lambda: 0.04,
      },
    ],
  },
};
